import { useSignMessage } from '@pancakeswap/wagmi'
import { useCallback, useEffect, useState } from 'react'
import { useAccount } from 'wagmi'
import { ApiError, Profile, Session, api, loadSession, saveSession } from './api'
import { readRef } from './refstore'

/**
 * The connected wallet's CADINU session. Signing in = signing one EIP-4361 text the server wrote (free, no transaction).
 * The first sign-in of a wallet creates its CADINU user; an address alone never does.
 */
export function useSocialSession() {
  const { address } = useAccount()
  const { signMessageAsync } = useSignMessage()
  const [session, setSession] = useState<Session | null>(null)
  const [me, setMe] = useState<Profile | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const s = loadSession(address)
    setSession(s)
    setMe(null)
    // keep the session through network or server errors; only an answer that the token is invalid (401) ends it
    if (s)
      api<Profile>('me', { token: s.token })
        .then(setMe)
        .catch((e) => {
          if (e instanceof ApiError && e.status === 401) {
            saveSession(null)
            setSession(null)
          }
        })
  }, [address])

  const signIn = useCallback(async () => {
    if (!address) return null
    setBusy(true)
    setError(null)
    try {
      const n = await api<{ nonce: string; message: string }>('auth/nonce', { method: 'POST', body: { address } })
      const signature = await signMessageAsync({ message: n.message })
      const v = await api<{ token: string; expiry: string; profile: Profile }>('auth/verify', { method: 'POST', body: { address, nonce: n.nonce, signature, ref: readRef() } })
      const s = { address, token: v.token, expiry: v.expiry }
      saveSession(s)
      setSession(s)
      setMe(v.profile)
      return s
    } catch (e: any) {
      setError(e?.name === 'UserRejectedRequestError' || /reject|denied/i.test(String(e?.message)) ? 'Signature cancelled.' : 'Sign-in failed. Try again.')
      return null
    } finally {
      setBusy(false)
    }
  }, [address, signMessageAsync])

  const signOut = useCallback(() => {
    if (session) api('auth/logout', { method: 'POST', token: session.token }).catch(() => undefined)
    saveSession(null)
    setSession(null)
    setMe(null)
  }, [session])

  return { address, session, me, setMe, signIn, signOut, busy, error }
}
